/* Presentation for the supported agent kinds: a label, a bare currentColor
 * glyph, a Badge tone, plus per-kind flags (onboarding tile, docs integration).
 * Used by agent cards, the overview roster, the kind pickers (onboarding grid
 * + agent-detail dropdown), and the docs snippet picker.
 *
 * The kind VALUES come from ./kindRegistry — the single source of truth. The
 * exhaustive Record below means adding a value there fails this build until
 * its presentation is filled in. */

import { type ComponentType } from "react";
import {
  ClaudeGlyph,
  CodexGlyph,
  GlobeGlyph,
  LangChainGlyph,
} from "./logos/glyphs";
import {
  AGENT_KIND_VALUES,
  DEFAULT_AGENT_KIND,
  type AgentIntegration,
  type AgentKind,
} from "./kindRegistry";
import type { BadgeTone } from "../design-system";

export { AGENT_KIND_VALUES, DEFAULT_AGENT_KIND };
export type { AgentIntegration, AgentKind };

export interface AgentKindSpec {
  value: AgentKind;
  label: string;
  Icon: ComponentType<{ className?: string }>;
  tone: BadgeTone;
  /** The docs-snippet integration that best matches this kind. */
  integration: AgentIntegration;
  /** Offered as a tile in the onboarding grid. False for the kinds that are a
   * hand-configuration guide rather than a one-click path: as peers of the
   * configured kinds they invite the reader onto the hardest route. They stay
   * in the registry — stored agents still render label, glyph and tone. */
  onboardingTile: boolean;
}

/* Icons are the design's bare currentColor glyphs (Figma 199:8794). */
const PRESENTATION: Record<AgentKind, Omit<AgentKindSpec, "value">> = {
  claude_code: {
    label: "Claude Code",
    Icon: ClaudeGlyph,
    tone: "brand",
    integration: "claude-code",
    onboardingTile: true,
  },
  langchain: {
    label: "LangChain",
    Icon: LangChainGlyph,
    tone: "glacier",
    integration: "langchain",
    onboardingTile: true,
  },
  codex: {
    label: "Codex",
    Icon: CodexGlyph,
    tone: "info",
    integration: "codex",
    onboardingTile: true,
  },
  // cursor is parked with its registry value; it needs a glyph again (it had a
  // Phosphor stand-in; it's absent from the design tile set).
  other: {
    label: "Other",
    Icon: GlobeGlyph,
    tone: "neutral",
    integration: "generic",
    onboardingTile: false,
  },
};

/** Every supported kind's spec, in the registry's (design-grid) order. */
export const AGENT_KINDS: AgentKindSpec[] = AGENT_KIND_VALUES.map((value) => ({
  value,
  ...PRESENTATION[value],
}));

/** The kinds the onboarding grid offers as tiles — the configured paths. */
export const ONBOARDING_AGENT_KINDS: AgentKindSpec[] = AGENT_KINDS.filter(
  (k) => k.onboardingTile,
);

/** The kind behind the hand-configuration guide the onboarding grid links out to. */
export const MANUAL_AGENT_KIND: AgentKind = "other";

const BY_VALUE = new Map(AGENT_KINDS.map((k) => [k.value as string, k]));

const OTHER: AgentKindSpec = { value: "other", ...PRESENTATION.other };

/** Spec for a kind string, tolerating unknown/empty kinds ("" in a rollup). */
export function kindSpec(kind: string): AgentKindSpec {
  return BY_VALUE.get(kind) ?? OTHER;
}
